import type { Id } from '@odhaczka/core';
import { useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { ListReceipts } from '../components/receipts/ListReceipts';
import { Snackbar, type SnackbarMessage } from '../components/shop/Snackbar';
import { CHIP_CLS, plural, statusChip } from '../components/shop/status';
import { SuggestionBanner } from '../components/shop/SuggestionBanner';
import { StorePicker } from '../components/StorePicker';
import { PageHeader } from '../components/ui/PageHeader';
import { primaryBtn, secondaryBtn } from '../components/ui/classes';
import { SkeletonRows } from '../components/ui/Skeleton';
import { memberFirstName, useMembers } from '../data/household';
import { archiveList, newListFrom, quantityLabel, useList, useListItems } from '../data/lists';
import { copyItemsToList, missingText, nextListFor, reopenList } from '../data/shopping';
import { useStore } from '../data/stores';
import type { ListItemRow } from '../db/types';
import { currentUserId } from '../lib/auth';

// Kolejność na liście problemów: najpierw braki, na końcu nieodhaczone.
const RANK: Record<ListItemRow['status'], number> = { missing: 0, partial: 1, substitute: 2, pending: 3, cart: 4 };

/** Po zakupach: podsumowanie i co zrobić z brakami (wysłać, przenieść, dokupić gdzie indziej). */
export function AfterPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const list = useList(id);
  const items = useListItems(id);
  const store = useStore(list?.storeId);
  const members = useMembers();
  const me = currentUserId();
  const [deselected, setDeselected] = useState<ReadonlySet<Id>>(() => new Set());
  const [moved, setMoved] = useState<ReadonlySet<Id>>(() => new Set());
  const [movedTo, setMovedTo] = useState<Id | null>(null);
  const target = useList(movedTo ?? undefined);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [snack, setSnack] = useState<SnackbarMessage | null>(null);
  const snackId = useRef(0);

  if (list === undefined || items === undefined)
    return (
      <div className="p-4">
        <SkeletonRows count={4} />
      </div>
    );
  if (list === null || list.deleted || !id)
    return (
      <div className="flex flex-col items-center gap-4 p-8 text-center">
        <p className="text-stone-600 dark:text-stone-400">Nie ma takiej listy — mogła zostać usunięta.</p>
        <Link to="/" className={primaryBtn}>
          Wróć do list
        </Link>
      </div>
    );

  const listId = id;
  const total = items.length;
  const count = (s: ListItemRow['status']) => items.filter((i) => i.status === s).length;
  const bought = count('cart');
  const problems = items.filter((i) => i.status !== 'cart').sort((a, b) => RANK[a.status] - RANK[b.status] || a.position - b.position);
  const selected = problems.filter((i) => !deselected.has(i.id));
  const none = selected.length === 0 || busy;
  const text = missingText(selected);

  const toast = (t: string, action?: SnackbarMessage['action']) => setSnack({ id: ++snackId.current, text: t, action });
  const toggle = (itemId: Id) =>
    setDeselected((prev) => {
      const next = new Set(prev);
      if (!next.delete(itemId)) next.add(itemId);
      return next;
    });
  const allSelected = selected.length === problems.length;
  const toggleAll = () => setDeselected(allSelected ? new Set(problems.map((i) => i.id)) : new Set());
  const what = (n: number) => `${n} ${plural(n, 'pozycję', 'pozycje', 'pozycji')}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      toast('Skopiowano');
    } catch {
      toast('Nie udało się skopiować');
    }
  };

  const send = async () => {
    if (typeof navigator.share !== 'function') return copy();
    try {
      await navigator.share({ text });
    } catch (e) {
      // Zamknięcie arkusza udostępniania to nie błąd.
      if (!(e instanceof DOMException && e.name === 'AbortError')) await copy();
    }
  };

  const moveToNext = async () => {
    const chosen = selected;
    setBusy(true);
    try {
      const targetId = await nextListFor(listId);
      // Pozycje, które już czekają na tamtej liście, są pomijane — liczy się tylko to, co faktycznie doszło.
      const added = await copyItemsToList(chosen, targetId);
      const ids = chosen.map((i) => i.id);
      setMoved((prev) => new Set([...prev, ...ids]));
      setDeselected((prev) => new Set([...prev, ...ids]));
      setMovedTo(targetId);
      const open = { label: 'Otwórz', onClick: () => navigate(`/lists/${targetId}`) };
      if (added === 0) toast('Wszystko już jest na następnej liście', open);
      else if (added < chosen.length) toast(`Przeniesiono ${added}, reszta już tam była`, open);
      else toast(`Przeniesiono ${what(added)}`, open);
    } catch {
      toast('Nie udało się przenieść');
    } finally {
      setBusy(false);
    }
  };

  const buyElsewhere = async (storeId: Id) => {
    const chosen = selected;
    setBusy(true);
    try {
      const targetId = await nextListFor(listId, storeId);
      await copyItemsToList(chosen, targetId);
      navigate(`/lists/${targetId}`);
    } catch {
      setBusy(false);
      toast('Nie udało się utworzyć listy');
    }
  };

  const backToList = async () => {
    await reopenList(listId).catch(() => {});
    navigate(`/lists/${listId}`);
  };

  const fromThis = async () => navigate('/new', { state: await newListFrom(list) });

  const archive = async () => {
    try {
      await archiveList(listId);
      navigate('/');
    } catch {
      toast('Nie udało się zarchiwizować');
    }
  };

  const segments = [
    { n: bought, cls: 'bg-brand-600' },
    { n: count('partial') + count('substitute'), cls: 'bg-amber-400' },
    { n: count('missing'), cls: 'bg-miss-500' },
    { n: count('pending'), cls: 'bg-stone-300 dark:bg-stone-700' },
  ];
  const summary = (
    [
      [count('missing'), plural(count('missing'), 'brak', 'braki', 'braków')],
      [count('partial'), 'częściowo'],
      [count('substitute'), plural(count('substitute'), 'zamiennik', 'zamienniki', 'zamienników')],
      [count('pending'), plural(count('pending'), 'nieodhaczona', 'nieodhaczone', 'nieodhaczonych')],
    ] as const
  )
    .filter(([n]) => n > 0)
    .map(([n, label]) => `${n} ${label}`)
    .join(' · ');

  return (
    <>
      <PageHeader title="Podsumowanie" back="/" />

      <main className="flex-1 pb-4">
        <section className="m-4 rounded-2xl border border-stone-200 bg-white p-5 shadow-sm dark:border-stone-800 dark:bg-stone-900">
          <p className="truncate text-sm text-stone-500">
            {list.title ?? 'Zakupy'}
            {store?.name ? ` · ${store.name}` : ''}
          </p>
          <p className="mt-1 text-3xl font-bold">
            Kupione {bought} z {total}
          </p>
          {total > 0 && (
            <div className="mt-3 flex h-2.5 overflow-hidden rounded-full bg-stone-100 dark:bg-stone-800" aria-hidden>
              {segments.map((s, i) => s.n > 0 && <div key={i} className={s.cls} style={{ width: `${(s.n / total) * 100}%` }} />)}
            </div>
          )}
          <p className="mt-3 text-stone-600 dark:text-stone-400">{problems.length === 0 ? 'Wszystko kupione — nic nie trzeba dokupić.' : summary}</p>
        </section>

        <ListReceipts list={list} lidlStore={`${store?.chainName ?? ''} ${store?.name ?? ''}`.toLowerCase().includes('lidl')} />

        {problems.length > 0 && (
          <section>
            <div className="flex items-center justify-between px-4 pt-2 pb-1.5">
              <h2 className="text-sm font-semibold tracking-wide text-stone-500 uppercase">Do załatwienia</h2>
              <button type="button" onClick={toggleAll} className="min-h-11 px-2 text-sm font-medium text-brand-700 dark:text-brand-500">
                {allSelected ? 'Odznacz wszystkie' : 'Zaznacz wszystkie'}
              </button>
            </div>
            <ul className="border-t border-stone-200 dark:border-stone-800">
              {problems.map((i) => (
                <ProblemRow
                  key={i.id}
                  item={i}
                  checked={!deselected.has(i.id)}
                  moved={moved.has(i.id)}
                  onToggle={() => toggle(i.id)}
                  suggestedBy={i.suggestedBy && i.suggestedBy === me ? null : memberFirstName(i.suggestedBy ? members.get(i.suggestedBy) : undefined)}
                />
              ))}
            </ul>

            <div className="flex flex-col gap-2 p-4">
              <div className="flex gap-2">
                <button type="button" disabled={none} onClick={() => void send()} className={`${primaryBtn} flex-1`}>
                  Wyślij ({selected.length})
                </button>
                {none ? (
                  <span className={`${secondaryBtn} w-auto! opacity-50`}>SMS</span>
                ) : (
                  <a href={`sms:?body=${encodeURIComponent(text)}`} className={`${secondaryBtn} w-auto!`}>
                    SMS
                  </a>
                )}
              </div>
              <button type="button" disabled={none} onClick={() => void moveToNext()} className={secondaryBtn}>
                Przenieś na następne zakupy
              </button>
              <button type="button" disabled={none} onClick={() => setPickerOpen(true)} className={secondaryBtn}>
                Dokup w innym sklepie
              </button>
              {movedTo && target && (
                <p className="text-center text-sm text-stone-600 dark:text-stone-400">
                  Dodane do „{target.title ?? 'Zakupy'}” ·{' '}
                  <Link to={`/lists/${movedTo}`} className="font-medium text-brand-700 underline underline-offset-2 dark:text-brand-500">
                    otwórz listę
                  </Link>
                </p>
              )}
            </div>
          </section>
        )}
      </main>

      {/* Bez przyklejania — przy długiej liście braków zasłaniałby akcje powyżej. */}
      <div className="safe-bottom flex flex-col gap-2 border-t border-stone-200 px-4 pt-3 dark:border-stone-800">
        <div className="flex gap-2">
          <button type="button" onClick={() => void backToList()} className={secondaryBtn}>
            Wróć do listy
          </button>
          {list.status !== 'archived' && (
            <button type="button" onClick={() => void archive()} className={secondaryBtn}>
              Archiwizuj
            </button>
          )}
        </div>
        <button type="button" onClick={() => void fromThis()} className={secondaryBtn}>
          Nowa lista na podstawie tej
        </button>
        <Link to="/" className={primaryBtn}>
          Gotowe
        </Link>
      </div>

      <StorePicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onPick={(storeId) => void buyElsewhere(storeId)}
        title="Gdzie dokupić?"
        selectedId={list.storeId}
      />

      <Snackbar message={snack} onDismiss={() => setSnack(null)} offset="calc(env(safe-area-inset-bottom, 0px) + 1rem)" />
    </>
  );
}

interface ProblemRowProps {
  item: ListItemRow;
  checked: boolean;
  moved: boolean;
  onToggle: () => void;
  /** Imię proponującego zamiennik; null = moja propozycja. */
  suggestedBy: string | null;
}

function ProblemRow({ item, checked, moved, onToggle, suggestedBy }: ProblemRowProps) {
  const qty = quantityLabel(item);
  const chip = item.status === 'pending' ? { text: 'nieodhaczone', tone: 'neutral' as const } : statusChip(item);
  const detail =
    item.status === 'partial' && item.partialQty !== null
      ? `w koszyku ${item.partialQty}${item.qty !== null ? ` z ${item.qty}` : ''}`
      : item.status === 'substitute' && item.substitute
        ? `zamiast tego: ${item.substitute}`
        : null;
  return (
    <li>
      <label className="flex min-h-14 cursor-pointer items-center gap-3 border-b border-stone-200 bg-white px-4 active:bg-stone-50 dark:border-stone-800 dark:bg-stone-900 dark:active:bg-stone-800">
        <input type="checkbox" checked={checked} onChange={onToggle} className="size-5 shrink-0 accent-brand-700" />
        <div className="min-w-0 flex-1 py-2.5">
          <div className="text-lg leading-snug break-words">
            {item.name}
            {qty && <span className="ml-2 text-sm font-medium whitespace-nowrap text-stone-500">{qty}</span>}
          </div>
          {detail && <div className="text-sm text-stone-600 dark:text-stone-400">{detail}</div>}
          {item.note && <div className="text-sm text-amber-700 dark:text-amber-300">{item.note}</div>}
          {item.suggestion && <SuggestionBanner by={suggestedBy} text={item.suggestion} />}
          {moved && <div className="text-xs font-medium text-brand-700 dark:text-brand-500">przeniesione na następne zakupy</div>}
        </div>
        <span className={`shrink-0 rounded-full px-2.5 py-1 text-sm font-medium ${CHIP_CLS[chip.tone]}`}>
          {item.status === 'substitute' ? 'zamiennik' : chip.text}
        </span>
      </label>
    </li>
  );
}
